import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';

import hologramVert from './shaders/hologram.vert.glsl?raw';
import hologramFrag from './shaders/hologram.frag.glsl?raw';
import cornerBeamVert from './shaders/cornerBeam.vert.glsl?raw';
import cornerBeamFrag from './shaders/cornerBeam.frag.glsl?raw';

import {
  loadTrackPoints,
  createCurveFromPoints,
  buildHologramCurtain,
  buildCircuitStrokes,
  detectCorners,
  buildCornerMarker,
  buildCityFloor,
} from './trackBuilder.js';
import {
  DEFAULT_SETTINGS,
  DEFAULT_SVG,
  applySettingsDefaults,
  mergeSettings,
} from './settings.js';
import {
  initSettingsPanel,
  syncPanelFromSettings,
  resetPresetFileInputs,
  resetPlaceStartButton,
  syncLapDirectionButtons,
  resetLapDirectionButtons,
  setPresetStatus,
} from './settingsPanel.js';
import {
  clearPresetStorage,
  createPresetSnapshot,
  downloadPresetFile,
  loadPresetFromStorage,
  readPresetFromJsonText,
  savePresetWithFallback,
  textureToDataUrl,
} from './settingsStorage.js';
import {
  createMapBackgroundMesh,
  updateMapBackgroundMesh,
  disposeMapBackgroundMesh,
} from './mapBackground.js';
import {
  pickTrackParamFromPointer,
  getTrackPickTolerance,
  resolveCornerList,
  orderMarkersFromStart,
} from './cornerMarkers.js';
import { createMarkersList } from './markersList.js';
import {
  cornerKeyFromT,
  pickCornerMarkerFromEvent,
  createMapCornerLabel,
  setCornerLabelContent,
  billboardCornerLabel,
} from './cornerLabel.js';
import { createLapTour } from './tourCamera.js';
import {
  createTrackNameLabel,
  updateTrackNameLabel,
} from './trackNameLabel.js';
import {
  listDrivers,
  exportDriversForPreset,
  setDriversFromPreset,
  resetDriversToDefaults,
  getDriver,
  fileToDataUrl,
} from './drivers.js';
import { bootstrap as bootstrapApp } from './bootstrap.js';
import { dbPresetToStoredPreset } from './dataLoader.js';
import {
  applyDisplayFont,
  applyDisplayFontFromDataUrl,
  DEFAULT_DISPLAY_FONT_LABEL,
  getDisplayFontState,
  resetDisplayFont,
} from './displayFont.js';
import {
  markerDrivers,
  loadCornerMetaFromPreset,
  exportCornerMetaForPreset,
  setCornerDriver,
  setCornerDescription,
  clearCornerMeta,
} from './cornerMeta.js';
import {
  clearHotspotOverlayPosition,
  hideHotspotOverlay,
  isHotspotOverlayOnScreen,
  updateHotspotOverlayFromMarker,
  updateHotspotOverlayPosition,
} from './hotspotOverlay.js';
import { createIntroTimeline } from './introTimeline.js';
import { mountVenueHeaderFlag, mountMarkersPanelFlag } from './venueHeaderFlag.js';
import { mountIntroPlayStop } from './mountIntroPlayStop.jsx';
import {
  attachMarkerLabel,
  computeMarkerLabelOffsetY,
  disposeMarkerLabel,
  updateMarkerCornerLabel,
} from './markerCornerLabel.js';
import {
  applyStartMarkerRaceLabelScale,
  attachStartMarkerRaceLabel,
  disposeStartMarkerRaceLabel,
  updateStartMarkerRaceLabel,
} from './startMarkerRaceLabel.js';
import {
  loadFlagTexture,
  loadDirectionTexture,
  buildStartMarker,
  setFlagMeshWaveStrength,
  updateStartMarkerAnimation,
  disposeStartMarker,
} from './startMarker.js';
import { VENUE_HEADER_FLAG_TEXTURE_CHANGED_EVENT } from './venueHeaderFlagTexture.js';
import { setActivePresetContext, setCurrentStoragePaths, getCurrentStoragePaths } from './settingsStorage.js';
import { resolveStorageUrl } from './dataLoader.js';
import { hideAllPanels, showEditPanels } from './settingsUi.js';
import { applyVenueData } from './venueHeader.js';

// ─── Scene setup ────────────────────────────────────────────────────────────
const canvas = document.querySelector('#canvas');
const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
renderer.setSize(window.innerWidth, window.innerHeight);
renderer.setClearColor(0x000000, 1);
renderer.toneMapping = THREE.ACESFilmicToneMapping;
renderer.toneMappingExposure = 0.92;

const scene = new THREE.Scene();
scene.fog = new THREE.FogExp2(0x000000, 0.018);

const camera = new THREE.PerspectiveCamera(45, window.innerWidth / window.innerHeight, 0.1, 200);
const initialCameraPosition = new THREE.Vector3(6, 10, 12);
const initialCameraTarget = new THREE.Vector3(0, 4, 0);
camera.position.copy(initialCameraPosition);

const controls = new OrbitControls(camera, canvas);
controls.enableDamping = true;
controls.dampingFactor = 0.06;
controls.minDistance = 4;
controls.maxDistance = 35;
controls.maxPolarAngle = Math.PI * 0.48;
controls.target.set(0, 4, 0);
controls.autoRotate = DEFAULT_SETTINGS.autoRotate;
controls.autoRotateSpeed = DEFAULT_SETTINGS.autoRotateSpeed;

const composer = new EffectComposer(renderer);
composer.addPass(new RenderPass(scene, camera));
const bloomPass = new UnrealBloomPass(
  new THREE.Vector2(window.innerWidth, window.innerHeight),
  DEFAULT_SETTINGS.bloomStrength,
  0.35,
  0.55,
);
composer.addPass(bloomPass);

const mapCornerLabel = createMapCornerLabel();
scene.add(mapCornerLabel.object);

scene.add(new THREE.AmbientLight(0x0a1018, 0.25));

const trackContentGroup = new THREE.Group();
scene.add(trackContentGroup);

const circuitGroup = new THREE.Group();
trackContentGroup.add(circuitGroup);

const trackNameLabel = createTrackNameLabel();
trackContentGroup.add(trackNameLabel.mesh);

/** @type {THREE.Group | null} */
let cityGroup = null;
/** @type {THREE.Texture | null} */
let mapTexture = null;
/** @type {string | null} */
let mapObjectUrl = null;
/** @type {THREE.Mesh | null} */
let mapMesh = null;
const textureLoader = new THREE.TextureLoader();
/** @type {import('./settings.js').HologramSettings} */
const settings = {};
applySettingsDefaults(settings);

/** @type {string | null} Raw SVG string from upload; null = use DEFAULT_SVG URL */
let svgSource = null;
let svgFileLabel = 'Default (track.svg)';
/** @type {string | null} */
let mapFileLabel = null;

/** @type {'edit' | 'display'} */
let appMode = 'edit';
/** @type {object | null} */
let bootstrapCircuit = null;
/** @type {object | null} */
let bootstrapOrganization = null;

let hologramMaterial;
/** @type {THREE.Mesh | null} */
let curtainMesh = null;
/** @type {THREE.Group | null} */
let strokesGroup = null;
/** @type {THREE.Group[]} */
let cornerMarkers = [];
/** @type {THREE.CatmullRomCurve3 | null} */
let activeCurve = null;
/** @type {number[]} Parametric positions 0–1 for user-placed corners */
let customCornerTs = [];
/** @type {number[]} Parametric positions 0–1 for corners removed from the layout */
let excludedCornerTs = [];
const CORNER_T_SPACING = 0.025;

/** No-op: RaceProX uses manual Save → Supabase (not upstream auto-save) */
function scheduleAutoSave() {}
/** @type {Map<string, string>} Turn names keyed by parametric t */
const cornerNames = new Map();
/** @type {THREE.Group | null} */
let selectedCornerMarker = null;


/** @typedef {{ name: string; driverId: string; description: string }} PlacementDraft */

/** @returns {PlacementDraft} */
function defaultPlacementDraft() {
  return { name: 'Marker name', driverId: '', description: '' };
}

/** @type {PlacementDraft} */
let placementDraft = defaultPlacementDraft();
/** @type {number | null} */
let startMarkerT = null;
/** @type {THREE.Group | null} */
let startMarkerGroup = null;
let placeStartMode = false;

const cornerTourStatusEl = document.getElementById('corner-tour-status');
const cornerTourPlayBtn = document.getElementById('corner-tour-play');
const cornerTourStopBtn = document.getElementById('corner-tour-stop');
const cornerTourResetCameraBtn = document.getElementById('corner-tour-reset-camera');
const venueHeaderFlagEl = document.getElementById('venue-header-flag');
const venueHeaderFlag =
  venueHeaderFlagEl != null ? mountVenueHeaderFlag(venueHeaderFlagEl) : null;
const markersPanelFlagEl = document.getElementById('markers-panel-flag');
const markersPanelFlag =
  markersPanelFlagEl != null ? mountMarkersPanelFlag(markersPanelFlagEl) : null;

const introPlayStopMountEl = document.getElementById('intro-play-stop-root');
/** @type {ReturnType<typeof mountIntroPlayStop> | null} */
const introPlayStopApi =
  introPlayStopMountEl instanceof HTMLElement
    ? mountIntroPlayStop(introPlayStopMountEl, { onToggle: toggleIntroPlayback })
    : null;
const settingsReloadViewBtn = document.getElementById('settings-reload-view');

/** @type {ReturnType<typeof createIntroTimeline> | null} */
let introApi = null;
let introComplete = false;
let mainPlaySequenceActive = false;

const cornerTour = createLapTour({
  getCurve: () => activeCurve,
  getMarkers: () => cornerMarkers,
  getStartMarker: () => startMarkerGroup,
  getStartT: () => startMarkerT,
  getLapClockwise: () => settings.lapClockwise,
  minCornerSpacing: CORNER_T_SPACING,
  getLiftY: () => settings.trackLift,
  getWorldRoot: () => trackContentGroup,
  getDwellSeconds: () => settings.cornerTourDwell,
  getTravelSpeed: () => settings.cornerTourTravelSpeed,
  getTravelMin: () => settings.cornerTourTravelMin,
  getTravelMax: () => settings.cornerTourTravelMax,
  getTravelMultiplier: () => settings.cornerTourTravelMultiplier,
  onShowCorner: (marker, index, total) => {
    if (marker.userData.isStartMarker) {
      hideCornerLabel();
      if (cornerTourStatusEl) {
        cornerTourStatusEl.textContent = `Start line · ${index + 1} / ${total}`;
        cornerTourStatusEl.classList.remove('error');
      }
      return;
    }
    showCornerLabel(marker, { showCanvasLabel: false });
    setMarkerCanvasLabelVisible(marker, false);
    if (cornerTourStatusEl) {
      cornerTourStatusEl.textContent = `Marker ${index + 1} / ${total}`;
      cornerTourStatusEl.classList.remove('error');
    }
  },
  onHideCorner: () => {
    restoreTourFocusedMarkerLabels();
    hideCornerLabel();
  },
  onTravelProgress: (from, to, total) => {
    if (cornerTourStatusEl) {
      cornerTourStatusEl.textContent = `Lap view · ${from + 1} → ${to + 1} / ${total}`;
    }
  },
});

let rebuildToken = 0;

const markersList = createMarkersList({
  listEl: document.getElementById('markers-list'),
  emptyEl: document.getElementById('markers-empty'),
  countEl: document.getElementById('markers-count'),
  getOrderedMarkers: () =>
    orderMarkersFromStart(cornerMarkers, {
      startT: startMarkerT,
      clockwise: settings.lapClockwise,
      minSpacing: CORNER_T_SPACING,
    }),
  getCornerName: (key) => cornerNames.get(key) ?? 'Marker name',
  getCornerMeta: (key) => ({
    driverId: markerDrivers.get(key)?.driverId ?? null,
    description: markerDrivers.get(key)?.quote ?? '',
  }),
  listDrivers,
  onSelectMarker: (marker) => showCornerLabel(marker),
  onDeselect: () => hideCornerLabel({ collapseList: false }),
  onRename: (key, name, marker) => renameCorner(key, name, marker),
  onAssignDriver: (key, driverId, marker) => assignDriverToCorner(key, driverId, marker),
  onAssignDescription: (key, text, marker) =>
    assignDescriptionToCorner(key, text, marker),
  onDelete: (marker) => {
    const t = marker.userData.cornerT;
    if (typeof t === 'number' && Number.isFinite(t)) removeCornerAt(t);
  },
});

/** @param {number} t */
function isCornerExcluded(t) {
  return excludedCornerTs.some((et) => Math.abs(et - t) < CORNER_T_SPACING);
}

function refreshMarkersListDrivers() {
  markersList.refreshDrivers();
}

/** @type {{ refresh: () => void } | undefined} */
let driversPanelApi;

function syncMarkerDriverIds() {
  for (const marker of cornerMarkers) {
    const key = marker.userData.cornerKey;
    if (typeof key === 'string') {
      const id = markerDrivers.get(key)?.driverId ?? null;
      marker.userData.driverId = id && getDriver(id) ? id : null;
      if (id && !getDriver(id)) {
        setCornerDriver(key, null);
      }
    }
  }
}

driversPanelApi = undefined; // driversPanel.js removed — drivers from DB

const raycaster = new THREE.Raycaster();
const pointerNdc = new THREE.Vector2();

function createHologramMaterial() {
  return new THREE.ShaderMaterial({
    vertexShader: hologramVert,
    fragmentShader: hologramFrag,
    uniforms: {
      uTime: { value: 0 },
      uExtrudeDepth: { value: settings.extrudeDepth },
      uColorTop: { value: new THREE.Color(settings.curtainColorTop) },
      uColorBottom: { value: new THREE.Color(settings.curtainColorBottom) },
      uPulseSpeed: { value: settings.pulseSpeed },
      uScanSpeed: { value: settings.scanSpeed },
      uAnimSpeed: { value: settings.animationSpeed },
      uCurtainOpacity: { value: settings.curtainOpacity },
      uPulseAmount: { value: settings.pulseAmount },
      uScanAmount: { value: settings.scanAmount },
    },
    transparent: true,
    depthWrite: false,
    side: THREE.DoubleSide,
    blending: THREE.AdditiveBlending,
  });
}

function disposeObject3D(obj) {
  obj.traverse((child) => {
    if (child.geometry) child.geometry.dispose();
    if (child.material) {
      if (Array.isArray(child.material)) {
        child.material.forEach((m) => m.dispose());
      } else {
        child.material.dispose();
      }
    }
  });
}

function clearCircuit() {
  for (const child of [...circuitGroup.children]) {
    circuitGroup.remove(child);
    disposeObject3D(child);
  }
  curtainMesh = null;
  strokesGroup = null;
  cornerMarkers = [];
}

function applyStrokeSettings() {
  if (!strokesGroup) return;

  for (const mesh of strokesGroup.children) {
    const role = mesh.userData.strokeRole;
    if (!role) continue;

    const colorKey = `${role}StrokeColor`;
    const opacityKey = `${role}StrokeOpacity`;
    mesh.material.color.set(settings[colorKey]);
    mesh.material.opacity = settings[opacityKey];
  }
}

function applyVisualSettings() {
  controls.autoRotate = settings.autoRotate;
  controls.autoRotateSpeed = settings.autoRotateSpeed;

  bloomPass.strength = settings.bloomStrength;

  if (hologramMaterial) {
    const u = hologramMaterial.uniforms;
    u.uExtrudeDepth.value = settings.extrudeDepth;
    u.uColorTop.value.set(settings.curtainColorTop);
    u.uColorBottom.value.set(settings.curtainColorBottom);
    u.uPulseSpeed.value = settings.pulseSpeed;
    u.uScanSpeed.value = settings.scanSpeed;
    u.uAnimSpeed.value = settings.animationSpeed;
    u.uCurtainOpacity.value = settings.curtainOpacity;
    u.uPulseAmount.value = settings.pulseAmount;
    u.uScanAmount.value = settings.scanAmount;
  }

  applyStrokeSettings();
  applyMapBackground();
  applyCircuitRotation();
  applyTrackNameLabel();

  if (curtainMesh) {
    curtainMesh.visible =
      settings.showElevationCurtain && settings.trackLift > 0.02;
  }

  cornerMarkers.forEach(applyCornerMarkerVisuals);
}

function getCornerMarkerBuildOptions() {
  return {
    beamHeight: settings.cornerBeamHeight,
    ringRadius: settings.cornerRingRadius,
    beamRadius: settings.cornerBeamRadius,
    glowRadius: settings.cornerBeamGlowRadius,
    color: settings.cornerColor,
    ringOpacity: settings.cornerRingOpacity,
    coreOpacity: settings.cornerCoreOpacity,
    beamOpacity: settings.cornerBeamOpacity,
    glowOpacity: settings.cornerBeamGlowOpacity,
    glowStrength: settings.cornerBeamGlowStrength,
    topGlowOpacity: 0,
    showBeamGlow: settings.showCornerBeamGlow,
    beamVertexShader: cornerBeamVert,
    beamFragmentShader: cornerBeamFrag,
  };
}

function applyCornerMarkerVisuals(marker) {
  const tint = new THREE.Color(settings.cornerColor);

  marker.traverse((child) => {
    if (!child.isMesh || !child.userData.cornerPart) return;

    const part = child.userData.cornerPart;

    if (child.material.uniforms) {
      child.material.uniforms.uColor.value.copy(tint);
      child.material.uniforms.uAnimSpeed.value = settings.animationSpeed;
      child.material.uniforms.uGlowStrength.value = settings.cornerBeamGlowStrength;

      if (part === 'beamCore') {
        child.material.uniforms.uOpacity.value = settings.cornerBeamOpacity;
        child.material.uniforms.uGlowLayer.value = 0;
      } else if (part === 'beamGlow') {
        child.material.uniforms.uOpacity.value = settings.cornerBeamGlowOpacity;
        child.material.uniforms.uGlowLayer.value = 1;
        child.material.uniforms.uGlowStrength.value =
          settings.cornerBeamGlowStrength * 0.85;
      }
    } else if (child.material.color) {
      child.material.color.copy(tint);
    }

    switch (part) {
      case 'centerDot':
        child.material.opacity = settings.cornerCoreOpacity;
        break;
      case 'innerRing':
        child.material.opacity = settings.cornerRingOpacity * 0.95;
        break;
      case 'outerRing':
        child.material.opacity = settings.cornerRingOpacity * 0.42;
        break;
      case 'beamGlow':
        child.visible = settings.showCornerBeamGlow;
        break;
      case 'topFlare':
        child.material.opacity = 0;
        break;
      case 'groundPool':
        child.visible = settings.showCornerBeamGlow;
        child.material.opacity = settings.cornerBeamGlowOpacity * 0.65;
        break;
      default:
        break;
    }
  });
}

function syncElevationDepth() {
  settings.extrudeDepth = Math.max(0, settings.trackLift);
  if (hologramMaterial) {
    hologramMaterial.uniforms.uExtrudeDepth.value = settings.extrudeDepth;
  }
}

function updateCornerCountDisplay(count) {
  const removeAllBtn = /** @type {HTMLButtonElement | null} */ (
    document.getElementById('corner-remove-all')
  );
  if (removeAllBtn) removeAllBtn.disabled = count === 0;
  markersList.render();
}

function clearCornerMarkerMeshes() {
  for (const marker of cornerMarkers) {
    disposeMarkerLabel(marker);
    circuitGroup.remove(marker);
    disposeObject3D(marker);
  }
  cornerMarkers = [];
}

function spawnCornerMarkers(curve) {
  clearCornerMarkerMeshes();

  const autoCorners = settings.useAutoCorners
    ? detectCorners(curve, {
        curvatureThreshold: settings.curvatureThreshold,
        minSpacing: 0.035,
      })
    : [];

  const corners = resolveCornerList(curve, autoCorners, customCornerTs, {
    useAuto: settings.useAutoCorners,
    useCustom: true,
    minSpacing: CORNER_T_SPACING,
  }).filter((corner) => !isCornerExcluded(corner.t));

  updateCornerCountDisplay(corners.length);

  for (let i = 0; i < corners.length; i++) {
    const corner = corners[i];
    const key = cornerKeyFromT(corner.t);
    if (!cornerNames.has(key)) {
      cornerNames.set(key, 'Marker name');
    }

    const marker = buildCornerMarker(corner.position, getCornerMarkerBuildOptions());
    marker.userData.cornerT = corner.t;
    marker.userData.cornerKey = key;
    marker.userData.cornerName = cornerNames.get(key);
    marker.userData.cornerIndex = i;
    marker.userData.driverId = markerDrivers.get(key)?.driverId ?? null;
    marker.userData.description = markerDrivers.get(key)?.quote ?? '';
    attachMarkerLabel(marker, cornerNames.get(key) ?? 'Marker name', i, {
      labelScale: settings.cornerLabelScale,
      offsetY: computeMarkerLabelOffsetY(settings),
    });
    cornerMarkers.push(marker);
    circuitGroup.add(marker);
  }

  syncMarkerDriverIds();

  if (selectedCornerMarker) {
    const selectedKey = selectedCornerMarker.userData.cornerKey;
    const next =
      selectedKey &&
      cornerMarkers.find((m) => m.userData.cornerKey === selectedKey);
    if (next) {
      selectedCornerMarker = next;
      refreshSelectedCornerLabel();
    } else {
      hideCornerLabel();
    }
  }
}

function refreshCornerMarkers() {
  if (!activeCurve) return;
  if (cornerTour.isRunning()) stopCornerTour();
  spawnCornerMarkers(activeCurve);
}

function applyStartMarkerVisuals(marker) {
  const tint = new THREE.Color(0xffffff);
  marker.traverse((child) => {
    if (!child.isMesh || !child.userData.cornerPart) return;
    const part = child.userData.cornerPart;
    if (child.material.uniforms) {
      child.material.uniforms.uColor.value.copy(tint);
      child.material.uniforms.uAnimSpeed.value = settings.animationSpeed;
      if (part === 'beamCore') {
        child.material.uniforms.uOpacity.value = 0.45;
        child.material.uniforms.uGlowLayer.value = 0;
      } else if (part === 'beamGlow') {
        child.material.uniforms.uOpacity.value = 0.28;
        child.material.uniforms.uGlowLayer.value = 1;
      }
    } else if (child.material.color) {
      child.material.color.copy(tint);
    }
  });
}

async function refreshStartMarker() {
  if (startMarkerGroup) {
    disposeStartMarkerRaceLabel(startMarkerGroup);
    circuitGroup.remove(startMarkerGroup);
    disposeStartMarker(startMarkerGroup);
    startMarkerGroup = null;
  }

  if (startMarkerT == null || !Number.isFinite(startMarkerT) || !activeCurve) {
    return;
  }

  try {
    const [flagTex, directionTex] = await Promise.all([
      loadFlagTexture(),
      loadDirectionTexture(),
    ]);
    startMarkerGroup = buildStartMarker(
      activeCurve,
      startMarkerT,
      settings.trackLift,
      flagTex,
      directionTex,
      {
        lapClockwise: settings.lapClockwise,
        flagWaveStrength: settings.flagWaveStrength,
        beamVertexShader: cornerBeamVert,
        beamFragmentShader: cornerBeamFrag,
      },
    );
    applyStartMarkerVisuals(startMarkerGroup);
    if (bootstrapCircuit) {
      attachStartMarkerRaceLabel(
        startMarkerGroup,
        bootstrapCircuit,
        settings.cornerLabelScale,
      );
    }
    circuitGroup.add(startMarkerGroup);
    applyFlagWaveStrength();
  } catch (err) {
    console.error('Failed to build start marker:', err);
  }
}

function setStartMarkerAt(t) {
  if (!Number.isFinite(t)) return;
  if (cornerTour.isRunning()) stopCornerTour();
  startMarkerT = t;
  refreshStartMarker();
}

function clearStartMarker() {
  startMarkerT = null;
  if (startMarkerGroup) {
    disposeStartMarkerRaceLabel(startMarkerGroup);
    circuitGroup.remove(startMarkerGroup);
    disposeStartMarker(startMarkerGroup);
    startMarkerGroup = null;
  }
}

/**
 * @param {boolean} clockwise
 */
function setLapDirection(clockwise) {
  settings.lapClockwise = clockwise;
  syncLapDirectionButtons(clockwise);
  if (cornerTour.isRunning()) stopCornerTour();
  refreshStartMarker();
}

function addCustomCornerAt(t) {
  if (customCornerTs.some((ct) => Math.abs(ct - t) < CORNER_T_SPACING)) return;
  customCornerTs.push(t);
  customCornerTs.sort((a, b) => a - b);
  excludedCornerTs = excludedCornerTs.filter(
    (et) => Math.abs(et - t) >= CORNER_T_SPACING,
  );
  const key = cornerKeyFromT(t);
  applyDraftToCornerKey(key);
  refreshCornerMarkers();
  const marker = cornerMarkers.find((m) => m.userData.cornerKey === key);
  if (marker) {
    showCornerLabel(marker, { showCanvasLabel: false });
  }
}

function clearCustomCorners() {
  customCornerTs = [];
  refreshCornerMarkers();
}

/**
 * @param {number} t
 */
function removeCornerAt(t) {
  if (!Number.isFinite(t)) return;

  customCornerTs = customCornerTs.filter(
    (ct) => Math.abs(ct - t) >= CORNER_T_SPACING,
  );
  if (!isCornerExcluded(t)) {
    excludedCornerTs.push(t);
    excludedCornerTs.sort((a, b) => a - b);
  }

  const key = cornerKeyFromT(t);
  cornerNames.delete(key);
  clearCornerMeta(key);

  if (
    selectedCornerMarker &&
    Math.abs((selectedCornerMarker.userData.cornerT ?? -1) - t) <
      CORNER_T_SPACING
  ) {
    hideCornerLabel();
  }

  refreshCornerMarkers();
}

function removeSelectedCorner() {
  if (!selectedCornerMarker) return;
  const t = selectedCornerMarker.userData.cornerT;
  if (typeof t !== 'number' || !Number.isFinite(t)) return;
  removeCornerAt(t);
}

function applyCircuitRotation() {
  const rad = THREE.MathUtils.degToRad(settings.circuitRotationY);
  trackContentGroup.rotation.y = rad;
}

function restoreTrackNameOpacity() {
  if (!trackNameLabel.mesh) return;
  const show =
    settings.showTrackName && settings.trackNameText?.trim().length > 0;
  trackNameLabel.mesh.visible = show;
  if (!show) return;
  trackNameLabel.mesh.material.opacity =
    trackNameLabel.mesh.userData._introTargetOpacity ?? settings.trackNameOpacity;
}

async function applyTrackNameLabel() {
  await updateTrackNameLabel(trackNameLabel, settings);
  if (trackNameLabel.mesh) {
    trackNameLabel.mesh.userData._introTargetOpacity = settings.trackNameOpacity;
    if (introApi?.isPlaying()) {
      const show =
        settings.showTrackName && settings.trackNameText?.trim().length > 0;
      if (show) {
        trackNameLabel.mesh.material.opacity = 0;
      }
    }
  }
}

function syncTourButtons() {
  const running = cornerTour.isRunning();
  if (cornerTourPlayBtn) cornerTourPlayBtn.disabled = running;
  if (cornerTourStopBtn) cornerTourStopBtn.disabled = !running;
  if (cornerTourResetCameraBtn) {
    cornerTourResetCameraBtn.disabled =
      !running || !cornerTour.isUserCameraControl();
  }
  introPlayStopApi?.setDisabled(running && !mainPlaySequenceActive);
}

function syncIntroButton(playing) {
  introPlayStopApi?.sync(playing);
}

function initIntroTimeline() {
  introApi = createIntroTimeline({
    circuitGroup,
    curtainMesh,
    hologramMaterial,
    trackNameLabel,
    getMarkers: () =>
      startMarkerGroup ? [startMarkerGroup, ...cornerMarkers] : cornerMarkers,
    getLiftY: () => settings.trackLift,
    getSettings: () => settings,
    onPlayingChange: syncIntroButton,
    onComplete: () => {
      introComplete = true;
      if (mainPlaySequenceActive) {
        startCornerTourFromSequence();
      } else {
        syncIntroButton(false);
      }
    },
    revealedInitially: true,
  });
  introComplete = true;
}

async function setupIntroAfterCircuit() {
  await applyTrackNameLabel();
  if (trackNameLabel.mesh) {
    trackNameLabel.mesh.userData._introTargetOpacity = settings.trackNameOpacity;
  }
  initIntroTimeline();
  restoreTrackNameOpacity();
}

function resetInitialView() {
  stopCornerTour();
  introApi?.pause();
  introApi?.applyEndState();
  restoreTrackNameOpacity();
  introComplete = true;
  mainPlaySequenceActive = false;
  syncIntroButton(false);


  if (placeStartMode) {
    setPlaceStartMode(false);
    resetPlaceStartButton();
  }

  hideCornerLabel();

  camera.position.copy(initialCameraPosition);
  controls.target.copy(initialCameraTarget);
  controls.autoRotate = settings.autoRotate;
  cornerTour.resetUserCameraControl();
  controls.update();
}

function toggleIntroPlayback() {
  if (!introApi) return;

  if (introApi.isPlaying()) {
    introApi.pause();
    introComplete = true;
    mainPlaySequenceActive = false;
    restoreTrackNameOpacity();
    return;
  }

  if (cornerTour.isRunning()) {
    if (mainPlaySequenceActive) {
      stopCornerTour();
    }
    return;
  }

  stopCornerTour();
  hideCornerLabel();
  introComplete = false;
  mainPlaySequenceActive = true;
  introApi.play();
}

function beginCornerTourPlayback() {
  const hasStart =
    startMarkerT != null && Number.isFinite(startMarkerT) && startMarkerGroup;
  if (!activeCurve || (!hasStart && cornerMarkers.length === 0)) {
    if (cornerTourStatusEl) {
      cornerTourStatusEl.textContent = 'Place a start line or markers to tour';
      cornerTourStatusEl.classList.add('error');
    }
    return false;
  }
  controls.autoRotate = false;

  if (placeStartMode) setPlaceStartMode(false);
  cornerTour.resetUserCameraControl();
  if (!cornerTour.start()) return false;
  syncTourButtons();
  if (cornerTourStatusEl) {
    cornerTourStatusEl.textContent = 'Playing…';
    cornerTourStatusEl.classList.remove('error');
  }
  return true;
}

function startCornerTourFromSequence() {
  if (!beginCornerTourPlayback()) {
    mainPlaySequenceActive = false;
    syncIntroButton(false);
    return;
  }
  syncIntroButton(true);
}

function startCornerTour() {
  mainPlaySequenceActive = false;
  introApi?.pause();
  syncIntroButton(false);
  beginCornerTourPlayback();
}

function stopCornerTour() {
  cornerTour.stop();
  restoreTourFocusedMarkerLabels();
  hideCornerLabel();
  if (mainPlaySequenceActive) {
    mainPlaySequenceActive = false;
    syncIntroButton(false);
  }
  syncTourButtons();
  if (cornerTourStatusEl) {
    cornerTourStatusEl.textContent = '';
  }
}

function syncPlacementRemoveButton() {}

function syncCornerMarkersHint() {}

/** @param {string} key */
function applyDraftToCornerKey(key) {
  const name = placementDraft.name.trim() || 'Marker name';
  cornerNames.set(key, name);
  const driverId = placementDraft.driverId.trim() || null;
  setCornerDriver(key, driverId);
  setCornerDescription(key, placementDraft.description);
}

/**
 * @param {{ collapseList?: boolean }} [options]
 */
function hideCornerLabel({ collapseList = true } = {}) {
  selectedCornerMarker = null;
  detachMapLabel();
  hideHotspotOverlay();
  if (collapseList) markersList.collapse();
}

/**
 * @param {THREE.Group | null | undefined} marker
 * @param {boolean} visible
 */
function setMarkerCanvasLabelVisible(marker, visible) {
  const labelObj = marker?.userData.cornerLabel?.object;
  if (labelObj) labelObj.visible = visible;
}

function restoreTourFocusedMarkerLabels() {
  for (const marker of cornerMarkers) {
    setMarkerCanvasLabelVisible(marker, true);
  }
}

/** Opacity multipliers for non-focused markers while one corner is highlighted. */
const FOCUS_DIM = {
  body: 0.2,
  ring: 0.08,
  label: 0.18,
};

function getFocusedCornerMarker() {
  if (!introComplete) return null;
  if (cornerTour.isRunning()) {
    return cornerTour.getPhase() === 'dwell' ? cornerTour.getActiveMarker() : null;
  }
  return selectedCornerMarker;
}

/**
 * @param {string} part
 * @param {boolean} dimInactive
 */
function markerPartDimMultiplier(part, dimInactive) {
  if (!dimInactive) return 1;
  if (part === 'innerRing' || part === 'outerRing') return FOCUS_DIM.ring;
  return FOCUS_DIM.body;
}

/**
 * @param {THREE.Object3D} labelRoot
 * @param {boolean} dimInactive
 */
function applyMarkerLabelFocusOpacity(labelRoot, dimInactive) {
  labelRoot.traverse((child) => {
    if (!child.isMesh || child.material?.opacity == null) return;
    if (child.userData._labelBaseOpacity == null) {
      child.userData._labelBaseOpacity = child.material.opacity;
    }
    const base = child.userData._labelBaseOpacity;
    child.material.opacity = dimInactive ? base * FOCUS_DIM.label : base;
  });
}

function refreshSelectedCornerLabel({ showCanvasLabel = true } = {}) {
  if (!selectedCornerMarker) return;

  const index = selectedCornerMarker.userData.cornerIndex ?? 0;
  const name =
    selectedCornerMarker.userData.cornerName ?? 'Marker name';

  setCornerLabelContent(mapCornerLabel, name, index);

  const key =
    selectedCornerMarker.userData.cornerKey ??
    cornerKeyFromT(selectedCornerMarker.userData.cornerT ?? 0);
  const driverId = markerDrivers.get(key)?.driverId ?? null;
  const description = markerDrivers.get(key)?.quote ?? '';

  if (introComplete) {
    if (showCanvasLabel) {
      attachMapLabelToMarker(selectedCornerMarker);
    }
    updateHotspotOverlayFromMarker(selectedCornerMarker);
  }
}

function selectedCornerKey() {
  if (!selectedCornerMarker) return null;
  return (
    selectedCornerMarker.userData.cornerKey ??
    cornerKeyFromT(selectedCornerMarker.userData.cornerT ?? 0)
  );
}



function applyCornerLabelScale() {
  mapCornerLabel.setScale(settings.cornerLabelScale);
  const offsetY = computeMarkerLabelOffsetY(settings);
  for (const marker of cornerMarkers) {
    marker.userData.cornerLabel?.setScale(settings.cornerLabelScale);
    const labelObj = marker.userData.cornerLabel?.object;
    if (labelObj) labelObj.position.y = offsetY;
  }
  if (startMarkerGroup) {
    applyStartMarkerRaceLabelScale(startMarkerGroup, settings.cornerLabelScale);
  }
  if (mapCornerLabel.object.visible && selectedCornerMarker) {
    mapCornerLabel.object.position.y = offsetY;
  }
}

function attachMapLabelToMarker(marker) {
  if (mapCornerLabel.object.parent && mapCornerLabel.object.parent !== marker) {
    mapCornerLabel.object.parent.remove(mapCornerLabel.object);
  }
  if (mapCornerLabel.object.parent !== marker) {
    marker.add(mapCornerLabel.object);
  }
  mapCornerLabel.object.position.set(0, computeMarkerLabelOffsetY(settings), 0);
  mapCornerLabel.object.visible = true;
}

function detachMapLabel() {
  if (mapCornerLabel.object.parent) {
    mapCornerLabel.object.parent.remove(mapCornerLabel.object);
  }
  scene.add(mapCornerLabel.object);
  mapCornerLabel.object.visible = false;
}

/**
 * @param {THREE.Group} marker
 * @param {{ showCanvasLabel?: boolean }} [options]
 */
function showCornerLabel(marker, { showCanvasLabel = true } = {}) {
  selectedCornerMarker = marker;
  const key =
    marker.userData.cornerKey ??
    cornerKeyFromT(marker.userData.cornerT ?? 0);
  markersList.setExpanded(key);
  refreshSelectedCornerLabel({ showCanvasLabel });
}

/**
 * @param {string} key
 * @param {string} name
 * @param {THREE.Group} marker
 */
function renameCorner(key, name, marker) {
  const trimmed = name.trim();
  if (!trimmed) return;

  cornerNames.set(key, trimmed);
  marker.userData.cornerName = trimmed;
  const index = marker.userData.cornerIndex ?? 0;
  updateMarkerCornerLabel(marker, trimmed, index);
  if (selectedCornerMarker === marker) {
    refreshSelectedCornerLabel();
  }
  scheduleAutoSave();
}

/**
 * @param {string} key
 * @param {string} driverId
 * @param {THREE.Group} marker
 */
function assignDriverToCorner(key, driverId, marker) {
  const id = driverId?.trim() || null;
  setCornerDriver(key, id);
  marker.userData.driverId = id;
  if (selectedCornerMarker === marker) {
    updateHotspotOverlayFromMarker(marker);
  }
  scheduleAutoSave();
}

/**
 * @param {string} key
 * @param {string} text
 * @param {THREE.Group} marker
 */
function assignDescriptionToCorner(key, text, marker) {
  setCornerDescription(key, text);
  marker.userData.description = text;
  if (selectedCornerMarker === marker) {
    updateHotspotOverlayFromMarker(marker);
  }
  scheduleAutoSave();
}

function renameSelectedCorner(name) {
  if (!selectedCornerMarker) return;
  const key =
    selectedCornerMarker.userData.cornerKey ??
    cornerKeyFromT(selectedCornerMarker.userData.cornerT ?? 0);
  renameCorner(key, name, selectedCornerMarker);
}

function getActiveTrackPickTolerance() {
  return getTrackPickTolerance(settings);
}

function onCanvasCornerClick(event) {
  if (cornerTour.isRunning()) return;

  if (placeStartMode) {
    pickStartFromPointer(event);
    return;
  }

  const marker = pickCornerMarkerFromEvent(
    event,
    canvas,
    camera,
    raycaster,
    pointerNdc,
    cornerMarkers,
  );

  if (marker) {
    if (selectedCornerMarker === marker) {
      hideCornerLabel();
    } else {
      showCornerLabel(marker);
    }
    return;
  }

  const trackHit = pickTrackParamFromPointer(
    event,
    canvas,
    camera,
    raycaster,
    pointerNdc,
    activeCurve,
    settings.trackLift,
    getActiveTrackPickTolerance(),
  );
  if (trackHit) {
    addCustomCornerAt(trackHit.t);
    return;
  }

  hideCornerLabel();
}

function setPlaceStartMode(enabled) {
  placeStartMode = enabled;
  if (enabled) {
    hideCornerLabel();
  }
  controls.enabled = !placeStartMode;
  canvas.style.cursor = placeStartMode ? 'crosshair' : '';
}

function pickStartFromPointer(event) {
  const trackHit = pickTrackParamFromPointer(
    event,
    canvas,
    camera,
    raycaster,
    pointerNdc,
    activeCurve,
    settings.trackLift,
    getActiveTrackPickTolerance(),
  );
  if (!trackHit) return;
  setStartMarkerAt(trackHit.t);
}

const GEOMETRY_KEYS = new Set([
  'trackScale',
  'circuitFlip',
  'trackLift',
  'curvatureThreshold',
  'strokeSpacing',
  'strokeOffsetFlip',
  'showCenterStroke',
  'showInnerStroke',
  'showOuterStroke',
  'centerStrokeWeight',
  'innerStrokeWeight',
  'outerStrokeWeight',
]);

/**
 * @param {keyof typeof DEFAULT_SETTINGS} key
 * @param {number | string | boolean} value
 */
const CORNER_REBUILD_KEYS = new Set([
  'useAutoCorners',
  'curvatureThreshold',
]);

const CORNER_LAYOUT_KEYS = new Set([
  'cornerRingRadius',
  'cornerBeamHeight',
  'cornerBeamRadius',
  'cornerBeamGlowRadius',
  'showCornerBeamGlow',
]);

const CORNER_VISUAL_KEYS = new Set([
  'cornerColor',
  'cornerRingOpacity',
  'cornerCoreOpacity',
  'cornerBeamOpacity',
  'cornerBeamGlowOpacity',
  'cornerBeamGlowStrength',
]);

function onSettingsChange(key, value) {
  settings[key] = value;

  if (key === 'trackLift') {
    syncElevationDepth();
    applyTrackNameLabel();
    refreshStartMarker();
  }

  applyVisualSettings();

  if (CORNER_REBUILD_KEYS.has(key) || CORNER_LAYOUT_KEYS.has(key)) {
    refreshCornerMarkers();
    return;
  }

  if (CORNER_VISUAL_KEYS.has(key)) {
    cornerMarkers.forEach(applyCornerMarkerVisuals);
    return;
  }

  if (key === 'circuitRotationY') {
    applyCircuitRotation();
    return;
  }

  if (
    key === 'cornerTourDwell' ||
    key === 'cornerTourTravelSpeed' ||
    key === 'cornerTourTravelMultiplier' ||
    key === 'cornerTourTravelMin' ||
    key === 'cornerTourTravelMax'
  ) {
    scheduleAutoSave();
    return;
  }

  if (key === 'cornerLabelScale') {
    applyCornerLabelScale();
    return;
  }

  if (key === 'flagWaveStrength') {
    applyFlagWaveStrength();
    return;
  }

  if (
    key === 'trackNameText' ||
    key === 'showTrackName' ||
    key === 'trackNameFontSize' ||
    key === 'trackNameColor' ||
    key === 'trackNameOpacity' ||
    key === 'trackNamePosX' ||
    key === 'trackNamePosZ' ||
    key === 'trackNameRotation' ||
    key === 'mapOffsetX' ||
    key === 'mapOffsetZ' ||
    key === 'mapSize'
  ) {
    applyTrackNameLabel();
    return;
  }

  if (GEOMETRY_KEYS.has(key)) {
    rebuildCircuit();
  }
}

function applyMapBackground() {
  if (!mapMesh || !mapTexture) return;

  updateMapBackgroundMesh(mapMesh, {
    opacity: settings.mapOpacity,
    size: settings.mapSize,
    offsetX: settings.mapOffsetX,
    offsetZ: settings.mapOffsetZ,
    rotationDeg: settings.mapRotation,
    visible: settings.showMapBackground,
  });
}

function removeMapBackground() {
  if (mapObjectUrl) {
    URL.revokeObjectURL(mapObjectUrl);
    mapObjectUrl = null;
  }
  if (mapMesh) {
    trackContentGroup.remove(mapMesh);
    disposeMapBackgroundMesh(mapMesh);
    mapMesh = null;
  }
  if (mapTexture) {
    mapTexture.dispose();
    mapTexture = null;
  }
}

function installMapTexture(texture) {
  if (mapMesh) {
    trackContentGroup.remove(mapMesh);
    disposeMapBackgroundMesh(mapMesh);
    mapMesh = null;
  }
  if (mapTexture) {
    mapTexture.dispose();
  }

  mapTexture = texture;
  mapMesh = createMapBackgroundMesh(texture, {
    opacity: settings.mapOpacity,
    size: settings.mapSize,
    offsetX: settings.mapOffsetX,
    offsetZ: settings.mapOffsetZ,
    rotationDeg: settings.mapRotation,
  });
  mapMesh.visible = settings.showMapBackground;
  trackContentGroup.add(mapMesh);
  applyCircuitRotation();
}

async function loadMapFile(file) {
  const url = URL.createObjectURL(file);

  try {
    const texture = await textureLoader.loadAsync(url);
    if (mapObjectUrl) URL.revokeObjectURL(mapObjectUrl);
    mapObjectUrl = url;
    mapFileLabel = file.name;
    installMapTexture(texture);
    applyMapBackground();
  } catch (err) {
    URL.revokeObjectURL(url);
    console.error('Failed to load map image:', err);
    const status = document.getElementById('map-load-status');
    if (status) {
      status.textContent = 'Failed to load image';
      status.classList.add('error');
    }
  }
}

function resetMap() {
  removeMapBackground();
  mapFileLabel = null;
  const status = document.getElementById('map-load-status');
  if (status) {
    status.textContent = '';
    status.classList.remove('error');
  }
}

async function loadMapFromDataUrl(dataUrl, label) {
  try {
    const texture = await textureLoader.loadAsync(dataUrl);
    if (mapObjectUrl) URL.revokeObjectURL(mapObjectUrl);
    mapObjectUrl = null;
    mapFileLabel = label;
    installMapTexture(texture);
    applyMapBackground();
  } catch (err) {
    console.error('Failed to restore map image:', err);
    resetMap();
  }
}

async function loadDisplayFontFile(file) {
  try {
    const dataUrl = await fileToDataUrl(file);
    await applyDisplayFont(dataUrl, file.name);
  } catch (err) {
    console.error('Failed to load display font:', err);
    setPresetStatus('Could not load font file', 'error');
    const displayFontNameEl = document.getElementById('display-font-file-name');
    if (displayFontNameEl) {
      displayFontNameEl.textContent = getDisplayFontState().fileLabel;
    }
  }
}

function resetDisplayFontSetting() {
  resetDisplayFont();
  const displayFontNameEl = document.getElementById('display-font-file-name');
  if (displayFontNameEl) {
    displayFontNameEl.textContent = DEFAULT_DISPLAY_FONT_LABEL;
  }
}

/** @param {import('./settingsStorage.js').StoredPreset} preset */
async function restoreDisplayFontFromPreset(preset) {
  const fontDataUrl =
    preset.overlayFontDataUrl ?? preset.venueTitleFontDataUrl ?? null;
  const fontLabel =
    preset.overlayFontFileLabel ??
    preset.venueTitleFontFileLabel ??
    'Saved font';

  if (fontDataUrl) {
    await applyDisplayFontFromDataUrl(fontDataUrl, fontLabel);
  } else {
    resetDisplayFont();
  }

  const displayFontNameEl = document.getElementById('display-font-file-name');
  if (displayFontNameEl) {
    displayFontNameEl.textContent = getDisplayFontState().fileLabel;
  }
}

function applyFlagWaveStrength() {
  const strength = settings.flagWaveStrength;
  venueHeaderFlag?.setWaveStrength(strength);
  markersPanelFlag?.setWaveStrength(strength);
  if (startMarkerGroup?.userData.flagMesh) {
    setFlagMeshWaveStrength(startMarkerGroup.userData.flagMesh, strength);
  }
}

async function rebuildCircuit() {
  const token = ++rebuildToken;
  clearCircuit();

  if (!hologramMaterial) {
    hologramMaterial = createHologramMaterial();
  } else {
    applyVisualSettings();
  }

  const source = svgSource ?? DEFAULT_SVG;

  // If no SVG source is available and we're relying on the default file,
  // check that it actually exists before trying to parse it.
  if (!svgSource) {
    try {
      const probe = await fetch(DEFAULT_SVG, { method: 'HEAD' });
      if (!probe.ok) {
        console.warn('[rebuildCircuit] No SVG source available and default track.svg not found — circuit not linked or has no layout SVG.');
        const status = document.getElementById('load-status');
        if (status) {
          status.textContent = 'No circuit SVG available. Upload one in the Circuit SVG section, or link a circuit with an SVG layout.';
          status.classList.add('error');
        }
        // Still build the city floor so the scene isn't completely empty
        if (!cityGroup) {
          cityGroup = buildCityFloor(16, 50);
          trackContentGroup.add(cityGroup);
        }
        return;
      }
    } catch {
      // fetch failed (e.g. network error) — fall through and let loadTrackPoints handle it
    }
  }

  try {
    const { points } = await loadTrackPoints(source, {
      liftY: settings.trackLift,
      scale: settings.trackScale,
      flipY: settings.circuitFlip,
      samples: 900,
    });

    if (token !== rebuildToken) return;

    const curve = createCurveFromPoints(points);
    activeCurve = curve;
    syncElevationDepth();

    curtainMesh = buildHologramCurtain(curve, {
      segments: 500,
      extrudeDepth: settings.extrudeDepth,
      groundY: 0,
      halfWidth: 0.055,
      material: hologramMaterial,
    });
    curtainMesh.visible =
      settings.showElevationCurtain && settings.trackLift > 0.02;
    circuitGroup.add(curtainMesh);

    strokesGroup = buildCircuitStrokes(curve, settings);
    circuitGroup.add(strokesGroup);

    spawnCornerMarkers(curve);
    await refreshStartMarker();

    if (!cityGroup) {
      cityGroup = buildCityFloor(16, 50);
      trackContentGroup.add(cityGroup);
    }

    const box = new THREE.Box3().setFromPoints(points);
    const center = box.getCenter(new THREE.Vector3());
    controls.target.copy(center);
    initialCameraTarget.copy(center);

    const status = document.getElementById('load-status');
    if (status) {
      status.textContent = `Loaded: ${svgFileLabel}`;
      status.classList.remove('error');
    }

    await setupIntroAfterCircuit();
  } catch (err) {
    console.error('Failed to load track SVG:', err);
    const status = document.getElementById('load-status');
    if (status) {
      status.textContent = err instanceof Error ? err.message : 'Failed to load SVG';
      status.classList.add('error');
    }
  }
}


async function loadSvgFile(file) {
  const text = await file.text();
  svgSource = text;
  svgFileLabel = file.name;
  customCornerTs = [];
  excludedCornerTs = [];
  startMarkerT = null;
  cornerNames.clear();
  markerDrivers.clear();
  hideCornerLabel();
  document.getElementById('svg-file-name').textContent = file.name;
  await rebuildCircuit();
}

function resetSvg() {
  svgSource = null;
  svgFileLabel = 'Default (track.svg)';
  rebuildCircuit();
}

/**
 * @param {import('./settingsStorage.js').StoredPreset} preset
 */
async function applyStoredPreset(preset) {
  mergeSettings(settings, preset.settings);

  customCornerTs = preset.customCornerTs ? [...preset.customCornerTs] : [];
  excludedCornerTs = preset.excludedCornerTs ? [...preset.excludedCornerTs] : [];
  startMarkerT =
    preset.startMarkerT != null && Number.isFinite(preset.startMarkerT)
      ? preset.startMarkerT
      : null;
  cornerNames.clear();
  if (preset.cornerNames) {
    for (const [key, name] of Object.entries(preset.cornerNames)) {
      cornerNames.set(key, name);
    }
  }

  loadCornerMetaFromPreset(preset.markerDrivers);

  // P1-1: Track storage paths so they survive the next save round-trip
  setCurrentStoragePaths(preset);

  // Drivers are loaded from the DB via bootstrap — don't overwrite them here.
  // Only refresh the markers list to pick up any marker↔driver assignments.
  refreshMarkersListDrivers();

  if (preset.svgSource) {
    svgSource = preset.svgSource;
    svgFileLabel = preset.svgFileLabel || 'Custom track.svg';
  } else {
    svgSource = null;
    svgFileLabel = preset.svgFileLabel || 'Default (track.svg)';
  }

  const svgNameEl = document.getElementById('svg-file-name');
  if (svgNameEl) svgNameEl.textContent = svgFileLabel;

  // F3: Resolve storage paths to URLs for maps
  if (preset.mapStoragePath) {
    const url = resolveStorageUrl(preset.mapStoragePath);
    if (url) await loadMapFromDataUrl(url, preset.mapFileLabel || 'Saved map');
  } else if (preset.mapDataUrl) {
    await loadMapFromDataUrl(
      preset.mapDataUrl,
      preset.mapFileLabel || 'Saved map',
    );
  } else {
    resetMap();
  }

  const mapNameEl = document.getElementById('map-file-name');
  if (mapNameEl) mapNameEl.textContent = mapFileLabel || 'None';

  // F3: Resolve storage paths to URLs for display font
  if (preset.displayFontStoragePath) {
    const url = resolveStorageUrl(preset.displayFontStoragePath);
    if (url) await applyDisplayFontFromDataUrl(url, preset.overlayFontFileLabel || 'Saved font');
  } else {
    await restoreDisplayFontFromPreset(preset);
  }

  const circuitId =
    typeof preset.venueCircuitId === 'string' && preset.venueCircuitId.trim()
      ? preset.venueCircuitId
      : null;
  // Venue data applied from bootstrap, not circuit selectors

  window.dispatchEvent(
    new CustomEvent(VENUE_HEADER_FLAG_TEXTURE_CHANGED_EVENT),
  );

  syncPanelFromSettings(settings);
  syncLapDirectionButtons(settings.lapClockwise);
  applyVisualSettings();
  syncElevationDepth();
  applyFlagWaveStrength();
}

/**
 * @param {{ includeImages?: boolean }} [options]
 */
async function buildCurrentPresetSnapshot(options = {}) {
  const includeImages = options.includeImages !== false;

  let mapDataUrl = null;
  if (includeImages && mapTexture) {
    try {
      mapDataUrl = await textureToDataUrl(mapTexture);
    } catch (err) {
      console.warn('Could not encode map for save:', err);
    }
  }

  const driverList = exportDriversForPreset();
  const drivers = includeImages
    ? driverList
    : driverList.map(({ id, firstName, lastName }) => ({
        id,
        firstName,
        lastName,
        profileImage: '',
        numberImage: '',
      }));

  const metaExport = exportCornerMetaForPreset();
  const { dataUrl: overlayFontDataUrl, fileLabel: overlayFontFileLabel } =
    getDisplayFontState();
  const snapshot = createPresetSnapshot({
    venueCircuitId: bootstrapCircuit?.id ?? null,
    settings,
    customCornerTs,
    excludedCornerTs,
    cornerNames,
    markerDrivers: new Map(Object.entries(metaExport)),
    drivers,
    svgSource,
    svgFileLabel,
    mapDataUrl: includeImages ? mapDataUrl : null,
    mapFileLabel: includeImages ? mapFileLabel : null,
    overlayFontDataUrl: includeImages ? overlayFontDataUrl : null,
    overlayFontFileLabel: includeImages ? overlayFontFileLabel : null,
    venueTitleFontDataUrl: null,
    venueTitleFontFileLabel: null,
    venueHeaderFlagDataUrl: null,
    venueHeaderFlagFileLabel: null,
    startMarkerT,
  });

  // P1-1: Include current storage paths so existing assets are never lost
  const paths = getCurrentStoragePaths();
  snapshot.svgStoragePath = paths.svg;
  snapshot.mapStoragePath = paths.map;
  snapshot.displayFontStoragePath = paths.displayFont;

  return snapshot;
}

/**
 * @param {import('./settingsStorage.js').StoredPreset} preset
 * @param {{ hadMapTexture: boolean; successMessage: string }} options
 */
async function reportPresetSaveResult(preset, { hadMapTexture, successMessage }) {
  try {
    const { warnings } = await savePresetWithFallback(preset);
    const suffix = warnings.length > 0
      ? ` (⚠ ${warnings.join('; ')})`
      : '';
    if (!hadMapTexture || preset.mapDataUrl) {
      setPresetStatus(`${successMessage}${suffix}`, warnings.length > 0 ? 'warn' : undefined);
    } else {
      setPresetStatus(`${successMessage} (map could not be encoded)${suffix}`, 'warn');
    }
  } catch (err) {
    console.error('Failed to save settings:', err);
    setPresetStatus('Could not save — check console for details', 'error');
  }
}

async function saveUserSettings() {
  setPresetStatus('Saving…');
  const preset = await buildCurrentPresetSnapshot();
  await reportPresetSaveResult(preset, {
    hadMapTexture: Boolean(mapTexture),
    successMessage: 'Settings saved',
  });
}

async function exportUserSettings() {
  setPresetStatus('Exporting…');
  try {
    const preset = await buildCurrentPresetSnapshot({ includeImages: false });
    downloadPresetFile(preset);
    setPresetStatus('Exported preset (without images)');
  } catch (err) {
    console.error('Failed to export preset:', err);
    setPresetStatus('Could not export preset', 'error');
  }
}

async function importUserSettings(file) {
  let text;
  try {
    text = await file.text();
  } catch (err) {
    console.error('Failed to read preset file:', err);
    setPresetStatus('Could not read preset file', 'error');
    return;
  }

  const preset = readPresetFromJsonText(text);
  if (!preset) {
    setPresetStatus('Invalid or unsupported preset file', 'error');
    return;
  }

  if (
    !confirm(
      'Replace all current settings with the imported preset? Unsaved changes will be lost.',
    )
  ) {
    return;
  }

  setPresetStatus('Importing…');
  if (cornerTour.isRunning()) stopCornerTour();

  try {
    await applyStoredPreset(preset);
    syncElevationDepth();
    await rebuildCircuit();
    applyTrackNameLabel();
    applyCornerLabelScale();
    await reportPresetSaveResult(preset, {
      hadMapTexture: Boolean(preset.mapDataUrl),
      successMessage: 'Imported and saved',
    });
  } catch (err) {
    console.error('Failed to import preset:', err);
    setPresetStatus('Could not import preset', 'error');
  }
}

async function resetAllSettings() {
  if (cornerTour.isRunning()) stopCornerTour();

  clearPresetStorage();
  applySettingsDefaults(settings);

  svgSource = null;
  svgFileLabel = 'Default (track.svg)';
  customCornerTs = [];
  excludedCornerTs = [];
  startMarkerT = null;
  cornerNames.clear();
  markerDrivers.clear();
  setCurrentStoragePaths({});
  resetDriversToDefaults();
  refreshMarkersListDrivers();
  driversPanelApi?.refresh();
  mapFileLabel = null;
  resetDisplayFont();
  // Venue data applied from bootstrap, not circuit selectors
  window.dispatchEvent(
    new CustomEvent(VENUE_HEADER_FLAG_TEXTURE_CHANGED_EVENT),
  );

  hideCornerLabel();
  setPlaceStartMode(false);
  resetPlaceStartButton();
  resetLapDirectionButtons();
  clearStartMarker();
  resetPresetFileInputs();
  resetMap();

  const svgNameEl = document.getElementById('svg-file-name');
  if (svgNameEl) svgNameEl.textContent = svgFileLabel;
  const mapNameEl = document.getElementById('map-file-name');
  if (mapNameEl) mapNameEl.textContent = 'None';
  resetDisplayFontSetting();

  syncPanelFromSettings(settings);
  applyVisualSettings();
  syncElevationDepth();
  applyFlagWaveStrength();
  await rebuildCircuit();
  applyTrackNameLabel();
  setPresetStatus('Reset to defaults');
}

async function initFromBootstrap() {
  const statusEl = document.getElementById('hologram-status');

  try {
    const result = await bootstrapApp();

    if (result.error) {
      if (statusEl) {
        statusEl.textContent = result.error;
        statusEl.classList.add('is-error');
      }
      return;
    }

    appMode = result.mode;
    bootstrapCircuit = result.circuit;
    bootstrapOrganization = result.organization;

    if (result.presetId) {
      setActivePresetContext(
        result.preset?.organization_id ?? result.organization?.id ?? '',
        result.presetId
      );
    }

    // Apply venue header data from bootstrap
    applyVenueData({
      circuit: bootstrapCircuit,
      organization: bootstrapOrganization,
    });

    // Apply drivers from bootstrap
    if (result.drivers?.length) {
      setDriversFromPreset(result.drivers);
      refreshMarkersListDrivers();
    }

    // Apply preset if available
    if (result.preset) {
      const storedPreset = dbPresetToStoredPreset(result.preset);
      await applyStoredPreset(storedPreset);

      // F3: Apply SVG from storage path or inline source
      const storedSvgPath = result.preset.svg_storage_path;
      if (storedSvgPath) {
        const svgUrl = resolveStorageUrl(storedSvgPath);
        if (svgUrl) {
          try {
            const resp = await fetch(svgUrl);
            svgSource = await resp.text();
            svgFileLabel = 'Circuit SVG';
          } catch (err) {
            console.warn('[main] Failed to fetch SVG from storage:', err);
          }
        }
      } else if (result.preset.svg_source) {
        svgSource = result.preset.svg_source;
        svgFileLabel = 'Circuit SVG';
      }
      if (!svgSource && bootstrapCircuit?.svgSource) {
        svgSource = bootstrapCircuit.svgSource;
        svgFileLabel = bootstrapCircuit.label || 'Circuit SVG';
      }
    } else if (bootstrapCircuit?.svgSource) {
      svgSource = bootstrapCircuit.svgSource;
      svgFileLabel = bootstrapCircuit.label || 'Circuit SVG';
    }

    syncPanelFromSettings(settings);
    syncLapDirectionButtons(settings.lapClockwise);
    syncElevationDepth();
    await rebuildCircuit();
    applyCornerLabelScale();
    restoreTrackNameOpacity();
    applyFlagWaveStrength();

    // Mode-specific setup
    if (appMode === 'display') {
      hideAllPanels();
      // Hide play button in display mode — auto-play
      if (introPlayStopMountEl) introPlayStopMountEl.style.display = 'none';
      // Auto-play after a short delay
      setTimeout(() => {
        if (introApi) {
          introComplete = false;
          mainPlaySequenceActive = true;
          introApi.play();
          syncIntroButton(true);
        }
      }, 800);
    } else {
      showEditPanels();
      setPresetStatus('Loaded from database');
    }

    // Hide loading overlay
    if (statusEl) {
      statusEl.classList.add('is-hidden');
    }
  } catch (err) {
    console.error('[main] Bootstrap failed:', err);
    if (statusEl) {
      statusEl.textContent = 'Failed to initialize hologram';
      statusEl.classList.add('is-error');
    }
  }
}

initSettingsPanel({
  settings,
  onSettingsChange,
  onSvgFile: loadSvgFile,
  onResetSvg: resetSvg,
  onMapFile: loadMapFile,
  onResetMap: resetMap,
  onDisplayFontFile: loadDisplayFontFile,
  onResetDisplayFont: resetDisplayFontSetting,
  onClearCustomCorners: clearCustomCorners,
  onTogglePlaceStart: setPlaceStartMode,
  onClearStartMarker: clearStartMarker,
  onLapDirectionChange: setLapDirection,
  onSaveSettings: saveUserSettings,
  onResetAllSettings: resetAllSettings,
  onExportSettings: exportUserSettings,
  onImportPresetFile: importUserSettings,
});

canvas.addEventListener('pointerdown', (event) => {
  if (event.button !== 0) return;
  if (placeStartMode) {
    event.preventDefault();
    pickStartFromPointer(event);
    return;
  }
});

window.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  if (!selectedCornerMarker) return;
  const target = event.target;
  if (
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement
  ) {
    return;
  }
  event.preventDefault();
  hideCornerLabel();
});

canvas.addEventListener('click', (event) => {
  if (event.button !== 0) return;
  onCanvasCornerClick(event);
});

controls.addEventListener('start', () => {
  if (cornerTour.isRunning()) {
    cornerTour.setUserCameraControl(true);
    syncTourButtons();
  }
});

cornerTourPlayBtn?.addEventListener('click', startCornerTour);
cornerTourStopBtn?.addEventListener('click', stopCornerTour);
cornerTourResetCameraBtn?.addEventListener('click', () => {
  if (!cornerTour.isRunning()) return;
  cornerTour.resetUserCameraControl();
  syncTourButtons();
});
settingsReloadViewBtn?.addEventListener('click', resetInitialView);
syncTourButtons();
syncIntroButton(false);

initFromBootstrap();

// ─── Animation loop ───────────────────────────────────────────────────────────
const clock = new THREE.Clock();

function animate() {
  requestAnimationFrame(animate);

  const delta = clock.getDelta();
  const t = clock.getElapsedTime();

  if (hologramMaterial) {
    hologramMaterial.uniforms.uTime.value = t;
  }

  for (const marker of cornerMarkers) {
    marker.traverse((child) => {
      if (!child.isMesh || !child.material?.uniforms?.uAnimSpeed) return;
      child.material.uniforms.uTime.value = t;
      child.material.uniforms.uAnimSpeed.value = settings.animationSpeed;
    });
  }

  venueHeaderFlag?.tick(t, settings.animationSpeed);
  markersPanelFlag?.tick(t, settings.animationSpeed);

  if (startMarkerGroup) {
    updateStartMarkerAnimation(startMarkerGroup, t, settings.animationSpeed);
    startMarkerGroup.traverse((child) => {
      if (!child.isMesh || !child.material?.uniforms?.uAnimSpeed) return;
      child.material.uniforms.uTime.value = t;
      child.material.uniforms.uAnimSpeed.value = settings.animationSpeed;
    });
  }

  if (cornerTour.isRunning()) {
    cornerTour.tick(delta, camera, controls);
  }

  const cornerBase = settings.cornerRingOpacity;
  const cornerAmp = settings.cornerPulseAmount;
  const cornerSpeed = settings.cornerPulseSpeed * settings.animationSpeed;

  if (introComplete) {
    const focusedMarker = getFocusedCornerMarker();
    const focusActive = focusedMarker != null;

    const pulseMarkers = startMarkerGroup
      ? [startMarkerGroup, ...cornerMarkers]
      : cornerMarkers;

    for (let i = 0; i < pulseMarkers.length; i++) {
      const marker = pulseMarkers[i];
      const isFocused = marker === focusedMarker;
      const dimInactive = focusActive && !isFocused;
      const isHighlighted = focusActive && isFocused;
      const pulse = Math.sin(t * cornerSpeed + i * 0.7);
      const pulse2 = Math.sin(t * cornerSpeed * 1.2 + i);
      const amp = cornerAmp * (isHighlighted ? 1.6 : 1);

      marker.traverse((child) => {
        if (!child.isMesh || !child.userData.cornerPart) return;
        const part = child.userData.cornerPart;
        const dim = markerPartDimMultiplier(part, dimInactive);

        if (part === 'outerRing' || part === 'innerRing') {
          const scale = 1 + pulse * amp;
          child.scale.set(scale, scale, 1);
          const mult = part === 'innerRing' ? 0.95 : 0.42;
          const boost = isHighlighted ? 0.35 : 0;
          child.material.opacity =
            (cornerBase * mult + pulse2 * amp * 0.5 + boost) * dim;
        }

        if (part === 'centerDot') {
          child.material.opacity = settings.cornerCoreOpacity * dim;
        }

        if (part === 'topFlare') {
          child.material.opacity = 0;
        }

        if (part === 'groundPool') {
          child.material.opacity = settings.cornerBeamGlowOpacity * 0.65 * dim;
        }

        if (
          (part === 'beamCore' || part === 'beamGlow') &&
          child.material.uniforms
        ) {
          const baseOpacity =
            part === 'beamCore'
              ? settings.cornerBeamOpacity
              : settings.cornerBeamGlowOpacity;
          child.material.uniforms.uOpacity.value = baseOpacity * dim;

          const baseGlow =
            part === 'beamCore'
              ? settings.cornerBeamGlowStrength
              : settings.cornerBeamGlowStrength * 0.85;
          const strength = isHighlighted ? baseGlow * 1.35 : baseGlow;
          child.material.uniforms.uGlowStrength.value =
            strength * (1 + pulse2 * amp * 0.25) * (dimInactive ? FOCUS_DIM.body : 1);
        }
      });

      const labelObj = marker.userData.cornerLabel?.object;
      if (labelObj?.visible) {
        applyMarkerLabelFocusOpacity(labelObj, dimInactive);
      }
    }
  }

  const tourDwellMarker =
    cornerTour.isRunning() && cornerTour.getPhase() === 'dwell'
      ? cornerTour.getActiveMarker()
      : null;

  for (const marker of cornerMarkers) {
    const labelObj = marker.userData.cornerLabel?.object;
    if (!labelObj) continue;
    if (marker === tourDwellMarker) {
      labelObj.visible = false;
      continue;
    }
    if (!labelObj.visible) continue;
    marker.updateWorldMatrix(true, false);
    billboardCornerLabel(labelObj, camera);
  }

  if (mapCornerLabel.object.visible) {
    billboardCornerLabel(mapCornerLabel.object, camera);
  }

  const startRaceLabel = startMarkerGroup?.userData.raceLabel?.object;
  if (startMarkerGroup && startRaceLabel?.visible) {
    startMarkerGroup.updateWorldMatrix(true, false);
    billboardCornerLabel(startRaceLabel, camera);
  }

  const focusedMarker = getFocusedCornerMarker();
  if (focusedMarker?.userData.driverId) {
    updateHotspotOverlayPosition(
      focusedMarker,
      camera,
      canvas,
      settings.cornerBeamHeight,
    );
  } else if (!isHotspotOverlayOnScreen()) {
    clearHotspotOverlayPosition();
  }

  controls.update();
  composer.render();
}

animate();

window.addEventListener('resize', () => {
  const w = window.innerWidth;
  const h = window.innerHeight;
  camera.aspect = w / h;
  camera.updateProjectionMatrix();
  renderer.setSize(w, h);
  composer.setSize(w, h);
  bloomPass.resolution.set(w, h);
});
